import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import { pushStateToQuery } from '@lib/query-utils';
import { convertQueryStringToRedux } from '@lib/search-results';
import { FILTER_DEFAULTS } from '@lib/filter-defaults';
import propertyTypeMap from '@src/data/property-type-map.json';
import { daysSinceOptions, priceOptions } from '@src/content/results/filters';

// ---------------------------------------------------------

import Button from '@components/button';
import FilterDropdownButton from './filter-button';
import Modal from '@components/modal';

import Checkboxes from '@components/checkbox';
import RadioButtons from '@components/radio-button';
import MultipleSelect from '@components/select/minmax-select';
import Select from '@components/select';
import RangeSlider from '@components/range-slider';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  filter_apply_button,
  filter_clear_button,
  filters_grouping_header,
  filters_grouping,
  filter_header,
  filter_row,
  days_since_sold_container,
  desktop_only,
  mobile_only
} = styles;

// ---------------------------------------------------------

const seedFiltersFromQuery = (query) => {
  const { convertedRouterParams } = convertQueryStringToRedux(query || {});
  return { ...FILTER_DEFAULTS, ...convertedRouterParams };
};

// ---------------------------------------------------------

const Filters = () => {
  const router = useRouter();

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [filters, setFilters] = useState(() => seedFiltersFromQuery(router.query));

  // Re-seed filters when URL changes (back/forward nav, external navigation)
  useEffect(() => {
    if (router.isReady) {
      setFilters(seedFiltersFromQuery(router.query));
    }
  }, [router.query, router.isReady]);

  // ---------------------------------------------------------

  const updateFilter = (group, value) => {
    setFilters((prev) => ({ ...prev, [group]: value }));
  };

  const updateAllFilters = (group, value) => {
    const next = { ...filters, [group]: value || [] };

    // Handle daysSinceSold when status changes
    if (group === 'status') {
      if (value?.includes('Sold') && value?.length === 1) {
        next.daysSinceSold = 1095;
      } else {
        next.daysSinceSold = undefined;
      }
    }

    setFilters(next);
  };

  const handlePropertyTypeChange = (value) => {
    let subSelectIds = [];
    switch (value) {
      case '1':
        subSelectIds = ['1', '2'];
        break;
      case '2':
        subSelectIds = ['4'];
        break;
      default:
        subSelectIds = [];
    }
    setFilters((prev) => ({
      ...prev,
      propertyTypeId: value,
      propertySubSelectIds: subSelectIds
    }));
  };

  const handlePropertySubChange = (value) => {
    updateAllFilters('propertySubSelectIds', value);
  };

  const handleMinMaxChange = (id) => {
    const titleCaseId = id.charAt(0).toUpperCase() + id.slice(1);
    const minId = 'min' + titleCaseId;
    const maxId = 'max' + titleCaseId;
    return ({ min, max }) => {
      if (parseInt(min) < parseInt(max)) {
        setFilters((prev) => ({ ...prev, [minId]: min, [maxId]: max }));
      }
    };
  };

  const handleRangeChange = (id) => {
    const titleCaseId = id.charAt(0).toUpperCase() + id.slice(1);
    const minId = 'min' + titleCaseId;
    const maxId = 'max' + titleCaseId;
    return ({ min, max }) => {
      if (min < max) {
        setFilters((prev) => ({ ...prev, [minId]: min, [maxId]: max }));
      }
    };
  };

  // ---------------------------------------------------------

  const handleClearingFilters = (filterKeys) => {
    if (filterKeys === undefined) {
      const cleared = {
        ...FILTER_DEFAULTS,
        propertySubSelectIds: ['1', '2'],
        propertyType: ['1'],
        status: ['Active', 'Pending']
      };
      setFilters(cleared);
      pushStateToQuery(router, cleared, true);
    } else {
      const cleared = { ...filters };
      filterKeys.forEach((type) => {
        switch (type) {
          case 'status':
            cleared[type] = ['Active', 'Pending'];
            break;
          case 'propertySubSelectIds':
            cleared[type] = ['1', '2'];
            break;
          case 'propertyTypeId':
            cleared[type] = '1';
            break;
          default:
            cleared[type] = FILTER_DEFAULTS[type];
        }
      });
      setFilters(cleared);
    }
  };

  const handleApplyAllFilters = () => {
    pushStateToQuery(router, filters, true);
    setModalIsOpen(false);
  };

  // ---------------------------------------------------------

  const applyAllButton = (
    <footer>
      <Button
        className={filter_apply_button}
        type="button"
        label="Apply Filters"
        onClick={handleApplyAllFilters}
      />
    </footer>
  );

  const clearButton = (labels) => (
    <button className={filter_clear_button} onClick={() => handleClearingFilters(labels)}>
      Clear filters
    </button>
  );

  const showingSolds =
    filters.status?.length === 1 && filters.status?.includes('Sold');

  // ---------------------------------------------------------

  const statusAndTypeFilters = (
    <>
      <div className={filter_row}>
        <Checkboxes
          label="Search types"
          selections={filters.propertyStatus}
          onChange={(value) => updateAllFilters('propertyStatus', value)}
          selectAllLabel="All Properties"
          items={[
            { label: 'Price Changes', value: 'daysSinceLastPriceChange' },
            { label: 'New Listings', value: 'daysSinceNew' },
            { label: 'Open Houses', value: 'showOnlyOpenHouses' },
            { label: 'New Construction', value: 'showOnlyNewConstructions' },
            { label: 'Featured', value: 'featured' }
          ]}
          layout="half"
        />
      </div>
      <div className={filter_row}>
        <Checkboxes
          label="Property status"
          selections={filters.status}
          onChange={(value) => updateAllFilters('status', value)}
          items={[
            { label: 'Active' },
            { label: 'Pending' },
            { label: 'Coming Soon' },
            { label: 'Sold' }
          ]}
          layout="half"
          autoUnselectOnAll
        />
        {showingSolds && (
          <Select
            className={days_since_sold_container}
            id="daysSinceSold"
            label="Sold in the last"
            options={daysSinceOptions}
            selected={filters.daysSinceSold}
            onChange={(value) => updateFilter('daysSinceSold', value)}
          />
        )}
      </div>
      <div className={filter_row}>
        <RadioButtons
          additionalOptions
          label="Property type"
          groupName="propertyTypeId"
          items={propertyTypeMap}
          selection={filters.propertyTypeId}
          subSelections={filters.propertySubSelectIds}
          onChange={handlePropertyTypeChange}
          onSubChange={handlePropertySubChange}
        />
      </div>
    </>
  );

  const priceFilters = (
    <div className={filter_row}>
      <MultipleSelect
        label="Price"
        anyLabel="Any Price"
        id="price"
        defaultMin={FILTER_DEFAULTS.minPrice}
        defaultMax={FILTER_DEFAULTS.maxPrice}
        options={priceOptions}
        min={parseInt(filters.minPrice)}
        max={parseInt(filters.maxPrice)}
        onChangeMinMax={handleMinMaxChange('price')}
      />
    </div>
  );

  const bedAndBathFilters = (
    <>
      <div className={filter_row}>
        <RadioButtons
          label="Bedrooms"
          groupName="bedrooms"
          theme="with-label-inside"
          selection={filters.bedrooms}
          onChange={(value) => updateFilter('bedrooms', value)}
          items={[
            { label: 'Any', value: 0 },
            { label: '1+', value: 1 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
            { label: '5+', value: 5 }
          ]}
        />
      </div>
      <div className={filter_row}>
        <RadioButtons
          label="Bathrooms"
          groupName="bathrooms"
          theme="with-label-inside"
          selection={filters.bathrooms}
          onChange={(value) => updateFilter('bathrooms', value)}
          items={[
            { label: 'Any', value: 0 },
            { label: '1+', value: 1 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 },
            { label: '5+', value: 5 }
          ]}
        />
      </div>
    </>
  );

  const additionalFilters = (
    <>
      <div className={filter_row}>
        <RangeSlider
          anyLabel="Any ft²"
          label="Square feet"
          id="squareFootage"
          marksSuffix=" ft²"
          rangeMin={0}
          rangeMax={8000}
          interval={200}
          min={filters.minSquareFootage}
          max={filters.maxSquareFootage}
          onChangeMinMax={handleRangeChange('squareFootage')}
        />
      </div>
      <div className={filter_row}>
        <RadioButtons
          label="Garage spaces"
          groupName="garageSpaces"
          theme="with-label-inside"
          selection={filters.garageSpaces}
          onChange={(value) => updateFilter('garageSpaces', value)}
          items={[
            { label: 'Any', value: 0 },
            { label: '1+', value: 1 },
            { label: '2+', value: 2 },
            { label: '3+', value: 3 },
            { label: '4+', value: 4 }
          ]}
        />
      </div>
      <div className={filter_row}>
        <Checkboxes
          label="Basement"
          selections={filters.basementTypes}
          onChange={(value) => updateAllFilters('basementTypes', value)}
          items={[
            { label: 'Finished', value: 'hasBasementTypeFinished' },
            { label: 'Partially Finished', value: 'hasBasementTypePartiallyFinished' },
            { label: 'Unfinished', value: 'hasBasementTypeUnfinished' }
          ]}
          layout="half"
        />
      </div>
      <div className={filter_row}>
        <Checkboxes
          label="Heating type"
          selections={filters.heatingTypes}
          onChange={(value) => updateAllFilters('heatingTypes', value)}
          items={[
            { label: 'Gas', value: 'hasHeatTypeGas' },
            { label: 'Electric', value: 'hasHeatTypeElectric' },
            { label: 'Steam', value: 'hasHeatTypeSteam' },
            { label: 'Forced air', value: 'hasHeatTypeForcedAir' },
            { label: 'Heat pump', value: 'hasHeatTypeHeatPump' },
            { label: 'Gravity', value: 'hasHeatTypeGravity' },
            { label: 'Geothermal', value: 'hasHeatTypeGeothermal' }
          ]}
          layout="half"
        />
      </div>

      <div className={filter_row}>
        <RangeSlider
          label="Lot size"
          id="acres"
          anyLabel="Any Size"
          marksSuffix=" ac"
          rangeMin={0}
          rangeMax={500}
          interval={25}
          min={filters.minAcres}
          max={filters.maxAcres}
          onChangeMinMax={handleRangeChange('acres')}
        />
      </div>

      <div className={filter_row}>
        <Checkboxes
          label="Must-haves"
          selectAllLabel={null}
          selections={filters.mustHaves}
          onChange={(value) => updateAllFilters('mustHaves', value)}
          items={[
            { label: '1st floor bedroom', value: 'hasLevel1Bedroom' },
            { label: 'Formal dining', value: 'hasFormalDiningRoom' },
            { label: '1st floor bathroom', value: 'hasLevel1Bath' },
            { label: 'Living room', value: 'hasFamilyRoom' },
            { label: 'Swimming', value: 'hasSwimmingPool' },
            { label: 'Central air', value: 'hasCentralAir' }
          ]}
          layout="half"
        />
      </div>
    </>
  );

  const mobileFilters = (
    <div className={filters_grouping}>
      <header className={filters_grouping_header}>
        <h1>Filters</h1>
        {clearButton()}
      </header>
      {statusAndTypeFilters}
      {priceFilters}
      {bedAndBathFilters}
      {additionalFilters}
      {applyAllButton}
    </div>
  );

  // ---------------------------------------------------------

  return (
    <>
      <div className={mobile_only}>
        <Button label="Add filters" onClick={() => setModalIsOpen(true)} />
        <Modal
          onModalClose={() => setModalIsOpen(false)}
          modalIsOpen={modalIsOpen}
          content={mobileFilters}
          theme="no-padding"
        />
      </div>
      <div className={desktop_only}>
        <div className={filters_grouping}>
          <FilterDropdownButton label="Status & Type" onApply={handleApplyAllFilters}>
            <header className={filter_header}>
              {clearButton(['propertyStatus', 'status', 'propertyTypeId', 'propertySubSelectIds'])}
            </header>
            {statusAndTypeFilters}
          </FilterDropdownButton>

          <FilterDropdownButton label="Price" onApply={handleApplyAllFilters}>
            <header className={filter_header}>{clearButton(['minPrice', 'maxPrice'])}</header>
            {priceFilters}
          </FilterDropdownButton>

          <FilterDropdownButton label="Bed & Bath" onApply={handleApplyAllFilters}>
            <header className={filter_header}>{clearButton(['bedrooms', 'bathrooms'])}</header>
            {bedAndBathFilters}
          </FilterDropdownButton>

          <FilterDropdownButton label="Additional Filters" onApply={handleApplyAllFilters}>
            <header className={filter_header}>
              {clearButton([
                'minSquareFootage',
                'maxSquareFootage',
                'garageSpaces',
                'basementTypes',
                'heatingTypes',
                'minAcres',
                'maxAcres',
                'mustHaves'
              ])}
            </header>
            {additionalFilters}
          </FilterDropdownButton>
        </div>
      </div>
    </>
  );
};

Filters.propTypes = {};

export default Filters;
